import { useEffect, useState, type ReactNode } from 'react';
import { CHANNEL_LABELS, type Channel, type ContentStatus } from '@marketingbee/contracts';
import { localDate, weekStartOf, zonedToUtc } from '@marketingbee/domain/marketing';
import { Select, cn } from '@marketingbee/ui';
import { trpc, type RouterOutputs } from '../lib/trpc';
import { useWorkspaceSlug } from '../lib/workspace';
import { useCurrent } from './common';

export type BrandRow = RouterOutputs['brand']['list'][number];
export type BrandProfile = BrandRow['profile'];
export type ContentRow = RouterOutputs['content']['list'][number];

export const STATUS_LABELS: Record<ContentStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  changes_requested: 'Changes requested',
  approved: 'Approved',
  scheduled: 'Scheduled',
  publishing: 'Publishing',
  published: 'Published',
  failed: 'Failed',
  rejected: 'Rejected',
  archived: 'Archived',
};

/** Status colours: one table for pills, calendar chips and legends. */
export const STATUS_CLASSES: Record<ContentStatus, string> = {
  draft: 'bg-muted text-foreground/75',
  in_review: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
  changes_requested: 'bg-orange-500/15 text-orange-800 dark:text-orange-300',
  approved: 'bg-blue-500/12 text-blue-800 dark:text-blue-300',
  scheduled: 'bg-blue-500/12 text-blue-800 dark:text-blue-300',
  publishing: 'bg-violet-500/15 text-violet-800 dark:text-violet-300',
  published: 'bg-success/12 text-success',
  failed: 'bg-destructive/10 text-destructive',
  rejected: 'bg-foreground/[0.05] text-muted-foreground line-through decoration-1',
  archived: 'bg-foreground/[0.05] text-muted-foreground',
};

export function StatusPill({ status, className }: { status: ContentStatus; className?: string }) {
  return <span className={cn('inline-flex items-center rounded-md px-2 py-0.5 text-[12px] font-medium leading-5 whitespace-nowrap', STATUS_CLASSES[status], className)}>{STATUS_LABELS[status]}</span>;
}

export function ChannelBadge({ channel, className }: { channel: Channel | string; className?: string }) {
  const label = CHANNEL_LABELS[channel as Channel] ?? channel;
  return <span className={cn('inline-flex items-center rounded-md px-2 py-0.5 text-[12px] font-semibold leading-5 whitespace-nowrap shadow-[inset_0_0_0_1px_var(--color-border)]', className)}>{label}</span>;
}

export type IssueLike = { code: string; severity: 'block' | 'warn'; message: string };

export function IssueList({ issues, empty }: { issues: readonly IssueLike[]; empty?: ReactNode }) {
  if (!issues.length) return empty ? <p className="text-[13px] text-muted-foreground">{empty}</p> : null;
  return (
    <ul className="space-y-1.5" aria-label="Guardrail issues">
      {issues.map((i, n) => (
        <li key={`${i.code}-${n}`} className={cn('rounded-lg px-3 py-2 text-[13px]', i.severity === 'block' ? 'bg-destructive/[0.08] text-destructive' : 'bg-amber-500/12 text-amber-900 dark:text-amber-200')}>
          <span className="font-semibold">{i.severity === 'block' ? 'Must fix' : 'Check'}:</span> {i.message}
        </li>
      ))}
    </ul>
  );
}

/** "Tue 21 Oct, 09:30" in the brand's time zone. */
export function formatInZone(value: Date | string | null | undefined, timeZone: string, opts: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }): string {
  if (!value) return 'Not scheduled';
  const d = typeof value === 'string' ? new Date(value) : value;
  try {
    return new Intl.DateTimeFormat('en-CA', { ...opts, timeZone }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-CA', opts).format(d);
  }
}

export function timeInZone(value: Date | string, timeZone: string): string {
  return formatInZone(value, timeZone, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}

/** yyyy-mm-dd + n days. */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export { localDate, weekStartOf, zonedToUtc };

/** `datetime-local` value (wall time in `timeZone`) for an instant. */
export function toZonedInput(value: Date | string | null | undefined, timeZone: string): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  return `${localDate(d, timeZone)}T${timeInZone(d, timeZone)}`;
}

export function fromZonedInput(value: string, timeZone: string): Date | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value);
  return m ? zonedToUtc(m[1]!, m[2]!, timeZone) : null;
}

/** Owners, admins and managers approve and schedule; writers (reps) draft and submit. */
export function useCanManageContent(): boolean {
  const current = useCurrent();
  const role = current.data?.role;
  return role === 'owner' || role === 'admin' || role === 'manager';
}

const BRAND_KEY = 'mb-brand';

/**
 * The brand a page works on: the remembered one when it still exists, else the first. With more
 * than one brand, pages show <BrandPicker>.
 */
export function useActiveBrand() {
  const list = trpc.brand.list.useQuery();
  const slug = useWorkspaceSlug();
  const key = `${BRAND_KEY}:${slug ?? ''}`;
  const [selected, setSelected] = useState<string | null>(() => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  });
  const brands = list.data ?? [];
  const brand = brands.find((b) => b.id === selected) ?? brands[0] ?? null;
  useEffect(() => {
    if (!brand) return;
    try {
      localStorage.setItem(key, brand.id);
    } catch {
      // ignore
    }
  }, [brand, key]);
  return { list, brands, brand, setBrandId: setSelected };
}

export function BrandPicker({ brands, value, onChange }: { brands: BrandRow[]; value: string | undefined; onChange: (id: string) => void }) {
  if (brands.length < 2) return null;
  return <Select aria-label="Brand" className="w-56" value={value} onChange={(e) => onChange(e.target.value)} options={brands.map((b) => ({ value: b.id, label: b.name }))} />;
}

export function pillarName(profile: BrandProfile | undefined, id: string | null | undefined): string | null {
  if (!id) return null;
  return profile?.pillars.find((p) => p.id === id)?.name ?? id;
}

export function audienceName(profile: BrandProfile | undefined, id: string | null | undefined): string | null {
  if (!id) return null;
  return profile?.audiences.find((a) => a.id === id)?.name ?? id;
}

/** Copy text; returns false when the clipboard is unavailable. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
